import { Box, Button, Grid, Stack, Typography } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import get from "lodash/get";
import { useState } from "react";
import { useMutation, useQuery } from "react-query";
import { Link as RouterLink, useParams } from "react-router-dom";
import Breadcrumb from "../../components/Breadcrumb";
import MainLayout from "../../components/MainLayout";
import useAlert from "../../hooks/useAlert";
import useNotification from "../../hooks/useNotification";
import service from "../../services/room";
import unitService from "../../services/unit";
import queryDefault from "../../utils/queryDefault";
import LoadingPage from "../LoadingPage";

const rupiah = (number: any) => {
  return new Intl.NumberFormat('id-ID', {
    style: 'currency',
    currency: 'IDR',
  }).format(number)
};

const CustomNoRowOverlay = () => (
  <Box sx={{ pt: 5 }}>
    <Typography variant="body1" textAlign="center" fontWeight="600">
      Sorry, data is not found
    </Typography>
  </Box>
);

export default function RoomDetail() {
  const { id } = useParams();
  const { data, isLoading, refetch } = useQuery(
    "current-room",
    () => service.getRoomById(Number(id))
  );
  const alert = useAlert();
  const notification = useNotification();
  const [pageSize, setPageSize] = useState<number>(10);
  const [options, setOptions] = useState<any>({});

  const deleteUnit = useMutation(
    "delete-room",
    unitService.deleteUnit,
    {
      onSuccess: () => {
        refetch();
        notification.onOpen({
          message: "Unit was successfully deleted!",
          type: "success",
          position: "top",
        });
      },
      onError: (error: any) => {
        refetch();
        notification.onOpen({
          message:
            error?.response?.data?.message === undefined
              ? "Something went wrong"
              : error?.response?.data?.message,
          type: "error",
          position: "top",
        });
      },
      ...queryDefault,
    }
  );
  const onDelete = (id: number) => {
    alert.onClose();
    deleteUnit.mutate(id);
  };

  const onClickDelete = (id: number) => {
    alert.onOpen({
      title: "Confirm Delete?",
      message: "Are you sure want to delete this unit?",
      actions: [
        {
          color: "error",
          variant: "contained",
          children: "Delete",
          onClick: () => onDelete(id),
        },
        {
          color: "primary",
          variant: "outlined",
          children: "Cancel",
          onClick: () => alert.onClose(),
        },
      ],
    });
  };

  return isLoading ? (
    <LoadingPage />
  ) : (
    <MainLayout>
      <Breadcrumb
        label={get(data, "data.name") || ""}
        breadcrumbs={[
          { label: "Properties", href: `/app/properties/property` },
          { label: `Property ${get(data, "data.property_name")}`, href: `/app/properties/property/detail/${get(data, "data.property_id")}` },
          { label: `Room ${get(data, "data.name")}`, href: `/app/properties/property/room/detail/${id}` },
        ]}
      />

      <Grid container spacing={2}>
        <Grid item lg={6} xs={12}>
          <Stack sx={{ mt: 2 }}>
            <Typography variant="body1" color="grey.600" fontWeight="300">
              Name
            </Typography>
            <Typography
              variant="body1"
              color="green"
              fontWeight="700"
              sx={{ mt: 0.3 }}
            >
              {data?.data?.name}
            </Typography>
          </Stack>
          <Stack sx={{ mt: 2 }}>
            <Typography variant="body1" color="grey.600" fontWeight="300">
              Property Name
            </Typography>
            <Typography
              variant="body1"
              color="green"
              fontWeight="700"
              sx={{ mt: 0.3 }}
            >
              {data?.data?.property_name}
            </Typography>
          </Stack>
          <Stack sx={{ mt: 2 }}>
            <Typography variant="body1" color="grey.600" fontWeight="300">
              Description
            </Typography>
            <Typography
              variant="body1"
              color="green"
              fontWeight="700"
              sx={{ mt: 0.3 }}
            >
              {data?.data?.description}
            </Typography>
          </Stack>
          <Stack sx={{ mt: 2 }}>
            <Typography variant="body1" color="grey.600" fontWeight="300">
              Type
            </Typography>
            <Typography
              variant="body1"
              color="green"
              fontWeight="700"
              sx={{ mt: 0.3 }}
            >
              {data?.data?.type}
            </Typography>
          </Stack>
          <Stack sx={{ mt: 2 }}>
            <Typography variant="body1" color="grey.600" fontWeight="300">
              Facilities
            </Typography>
            <Typography
              variant="body1"
              color="green"
              fontWeight="700"
              sx={{ mt: 0.3 }}
            >
              {data?.data?.facilities.map((item: any) => item.name).join(", ")}
            </Typography>
          </Stack>
        </Grid>

        <Grid item lg={6} md={12}>
          <Stack sx={{ mt: 2 }}>
            <Typography variant="body1" color="grey.600" fontWeight="300">
              Daily Price
            </Typography>
            <Typography
              variant="body1"
              color="green"
              fontWeight="700"
              sx={{ mt: 0.3 }}
            >
              {rupiah(data?.data?.daily_price)}
            </Typography>
          </Stack>
          <Stack sx={{ mt: 2 }}>
            <Typography variant="body1" color="grey.600" fontWeight="300">
              Monthly Price
            </Typography>
            <Typography
              variant="body1"
              color="green"
              fontWeight="700"
              sx={{ mt: 0.3 }}
            >
              {rupiah(data?.data?.monthly_price)}
            </Typography>
          </Stack>
          <Stack sx={{ mt: 2 }}>
            <Typography variant="body1" color="grey.600" fontWeight="300">
              Half-Yearly Price
            </Typography>
            <Typography
              variant="body1"
              color="green"
              fontWeight="700"
              sx={{ mt: 0.3 }}
            >
              {rupiah(data?.data?.half_yearly_price)}
            </Typography>
          </Stack>
          <Stack sx={{ mt: 2 }}>
            <Typography variant="body1" color="grey.600" fontWeight="300">
              Yearly Price
            </Typography>
            <Typography
              variant="body1"
              color="green"
              fontWeight="700"
              sx={{ mt: 0.3 }}
            >
              {rupiah(data?.data?.yearly_price)}
            </Typography>
          </Stack>
        </Grid>

        <Grid item md={12} mt={3}>
          <Stack sx={{ mt: 2 }}>
            <Typography variant="h3">Photos</Typography>
            {data?.data?.photos.map((photo: any) => (
              <Box mt={2}>
                <img
                  src={process.env.REACT_APP_BACKEND_URL + photo.photo}
                  alt="Unit"
                  style={{
                    width: "100%",
                    borderRadius: "0.375rem",
                    objectFit: "cover",
                    alignSelf: "start"
                  }}
                />
              </Box>
            ))}
          </Stack>
        </Grid>
      </Grid>

      <Typography variant="h2" mt={3}>
        Unit List
      </Typography>

      <Box sx={{ mt: 2 }}>
        <Stack direction="row" spacing={1}>
          <Button
            variant="contained"
            component={RouterLink}
            to={`/app/properties/property/room/${id}/unit/create`}
          >
            ADD UNIT
          </Button>
        </Stack>
      </Box>

      <Box mt={2}>
        <DataGrid
          autoHeight
          loading={isLoading}
          getRowId={(record) => get(record, "id")}
          rows={get(data, "data.units", [])}
          rowCount={get(data, "data.total_unit", 0)}
          page={options.page_number}
          pageSize={pageSize}
          rowsPerPageOptions={[10, 20, 30]}
          pagination
          disableSelectionOnClick
          onPageChange={(newPage) => {
            setOptions((prev: any) => ({ ...prev, page_number: newPage }));
          }}
          onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
          components={{
            NoRowsOverlay: CustomNoRowOverlay,
          }}
          columns={[
            {
              field: "name",
              headerName: "Name",
              flex: 1,
              filterable: false,
              renderCell: (params) => {
                return (
                  <Stack>
                    <Typography
                      sx={{ textDecoration: "none" }}
                      component={RouterLink}
                      to={`/app/properties/property/unit/detail/${get(
                        params,
                        "row.id"
                      )}`}
                    >{params.value}</Typography>
                  </Stack>
                );
              },
            },
            {
              field: "description",
              headerName: "Description",
              flex: 1,
              filterable: false,
              renderCell: (params) => {
                return (
                  <Stack>
                    <Typography>{params.value}</Typography>
                  </Stack>
                );
              },
            },
            {
              field: "status",
              headerName: "Status",
              flex: 1,
              filterable: false,
              renderCell: (params) => {
                return (
                  <Stack>
                    <Typography>{params.value}</Typography>
                  </Stack>
                );
              },
            },
            {
              field: "actions",
              headerName: "Action",
              sortable: false,
              filterable: false,
              width: 200,
              renderCell: (params) => {
                return (
                  <Stack direction="row" spacing={1}>
                    <Button
                      variant="outlined"
                      color="primary"
                      component={RouterLink}
                      to={`/app/properties/property/unit/edit/${get(
                        params,
                        "row.id"
                      )}`}
                    >
                      Edit
                    </Button>

                    <Button
                      variant="outlined"
                      color="error"
                      onClick={() => onClickDelete(params.row.id)}
                    >
                      Delete
                    </Button>
                  </Stack>
                );
              },
            },
          ]}
          sx={{
            ".MuiDataGrid-columnHeaderTitle": {
              fontWeight: 700,
            },
          }}
        />
      </Box>
    </MainLayout>
  );
}
